import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Plus, ArrowLeft, Send } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const STATUS_TONE = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  awaiting_customer: "bg-slate-50 text-slate-700 border-slate-200",
  in_progress: "bg-[#165a71]/10 text-[#165a71] border-[#165a71]/30",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-50 text-slate-600 border-slate-200",
};

export function SupportTicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", kind: "question", priority: "normal" });
  useDocumentTitle("Support");

  const load = async () => {
    const { data } = await api.get("/support/tickets");
    setTickets(data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and body required"); return; }
    try {
      await api.post("/support/tickets", form);
      toast.success("Ticket created");
      setShowCreate(false);
      setForm({ subject: "", body: "", kind: "question", priority: "normal" });
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl" data-testid="support-tickets">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Support</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your tickets</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Chat with the GCW team about any issue.</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="ticket-new">
            <Plus className="h-4 w-4" /> New ticket
          </button>
        </div>

        {tickets.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Ticket className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No support tickets.</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="tickets-list">
            {tickets.map((t) => (
              <Link key={t.id} to={`/dashboard/support/${t.id}`} className="block rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71]/40" data-testid={`ticket-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{t.kind} · {t.priority}</div>
                    <div className="font-display font-semibold text-[#071b22] truncate">{t.subject}</div>
                    <div className="text-xs text-[#252A34]/60 mt-1">Last activity {t.last_activity_at ? new Date(t.last_activity_at).toLocaleString() : "—"}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-1 ${STATUS_TONE[t.status] || "bg-slate-50 border-slate-200"}`}>{t.status.replace(/_/g, " ")}</span>
                </div>
              </Link>
            ))}
          </ul>
        )}

        {showCreate && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-lg rounded-3xl bg-white p-6 border border-slate-200" data-testid="ticket-form">
              <div className="font-display text-lg font-semibold text-[#071b22] mb-2">New support ticket</div>
              <div className="grid gap-3">
                <input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="Subject" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="ticket-subject" />
                <textarea rows={5} value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} placeholder="Describe the issue…" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="ticket-body" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="ticket-kind">
                    {["question", "issue", "billing", "safety", "feature_request", "other"].map((k) => <option key={k}>{k}</option>)}
                  </select>
                  <select value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="ticket-priority">
                    {["low", "normal", "high", "urgent"].map((k) => <option key={k}>{k}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowCreate(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="ticket-submit">Submit</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

export function SupportTicketDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/support/tickets/${id}`); setData(data); } catch (err) { toast.error("Not found"); nav("/dashboard/support"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${id}/messages`, { body });
      setBody("");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); } finally { setSending(false); }
  };

  if (!data) return <DashboardShell><div className="text-[#252A34]/60">Loading…</div></DashboardShell>;
  const t = data.ticket;

  return (
    <DashboardShell>
      <div className="max-w-3xl">
        <button onClick={() => nav("/dashboard/support")} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-4" data-testid="ticket-back">
          <ArrowLeft className="h-4 w-4" /> Back to tickets
        </button>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-4" data-testid="ticket-header">
          <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{t.kind} · {t.priority}</div>
          <h1 className="mt-1 font-display text-2xl font-semibold text-[#071b22]">{t.subject}</h1>
          <div className="text-xs text-[#252A34]/60 mt-1">Status: <b>{t.status}</b> · opened {new Date(t.created_at).toLocaleString()}</div>
          <div className="mt-4 whitespace-pre-line text-sm text-[#252A34]">{t.body}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-4" data-testid="ticket-messages">
          {(data.messages || []).length === 0 ? (
            <div className="p-6 text-sm text-[#252A34]/60 text-center">No messages yet — say something below to get started.</div>
          ) : (
            <ul className="divide-y">
              {(data.messages || []).map((m) => (
                <li key={m.id} className={`p-4 ${m.internal ? "bg-amber-50" : ""}`} data-testid={`ticket-msg-${m.id}`}>
                  <div className="text-xs text-[#252A34]/60"><b>{m.sender_role}</b> · {new Date(m.created_at).toLocaleString()}{m.internal && <span className="ml-2 text-amber-700 uppercase tracking-wider text-[10px]">internal</span>}</div>
                  <div className="mt-1 text-sm text-[#252A34] whitespace-pre-line">{m.body}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {t.status !== "closed" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="ticket-respond">
            <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="ticket-reply" />
            <div className="mt-2 flex justify-end">
              <button onClick={send} disabled={sending || !body.trim()} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="ticket-send">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Reply"}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
